import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle, FiX, FiPhone } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const STORAGE_KEY = 'drivepal_misconduct_notice';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export default function MisconductPopup() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const lastSeen = Number(localStorage.getItem(STORAGE_KEY));
    const noticeIsDue = !lastSeen || Date.now() - lastSeen >= DAY_IN_MS;

    if (!noticeIsDue) return undefined;

    const timer = window.setTimeout(() => setIsOpen(true), 4500);
    return () => window.clearTimeout(timer);
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-brand-darkbg/85 p-4 backdrop-blur-sm"
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.88, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 24 }}
            transition={{ type: 'spring', stiffness: 120, damping: 18 }}
            className="relative my-auto w-full max-w-md overflow-hidden rounded-2xl border-t-8 border-brand-gold bg-brand-navy shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="misconduct-notice-title"
          >
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-1/2 top-0 h-24 w-64 -translate-x-1/2 rounded-full bg-brand-gold/10 blur-2xl" />
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="absolute right-2 top-2 z-30 flex h-12 w-12 touch-manipulation items-center justify-center rounded-full bg-white/10 text-white shadow-md transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
              aria-label="Close important safety notice"
            >
              <SafeIcon icon={FiX} className="pointer-events-none text-2xl" />
            </button>

            <div className="relative z-10 p-6 pt-8 sm:p-9">
              <div className="mb-5 flex items-center justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-brand-gold/40 bg-brand-gold/20 shadow-lg">
                  <SafeIcon icon={FiAlertTriangle} className="text-4xl text-brand-gold" />
                </div>
              </div>

              <h2
                id="misconduct-notice-title"
                className="mb-2 pr-8 text-center font-display text-2xl leading-snug text-white md:text-3xl"
              >
                Important Safety Notice
              </h2>
              <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-brand-gold" />

              <p className="mb-8 text-center text-base leading-relaxed text-white/80 md:text-lg">
                To report misconduct by a driving instructor, call{' '}
                <strong className="text-white">Transport for NSW</strong> on
              </p>

              <a
                href="tel:131500"
                className="mb-6 flex touch-manipulation items-center justify-center gap-3 rounded-xl bg-brand-gold py-4 font-display text-2xl text-brand-navy shadow-lg transition-transform hover:scale-[1.02] active:scale-95"
              >
                <SafeIcon icon={FiPhone} className="text-2xl" />
                131 500
              </a>

              <p className="text-center text-xs leading-relaxed text-white/50">
                This notice is displayed in accordance with NSW driving instructor regulations.
              </p>

              <button
                type="button"
                onClick={handleClose}
                className="mt-5 w-full touch-manipulation rounded-xl border border-white/10 bg-white/10 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
              >
                I Understand — Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}